import { useGetJobAtsLink } from "@/lib/api/generated";
import type { CapabilitySnapshot } from "@/features/ats-sync/types";

// Read surface for connection sync settings lives in use-sync-settings.ts (the
// single query+mutation module). Re-exported here so existing call sites — the
// add-to-job modal imports it from this module — keep working.
export { useConnectionSyncSettings } from "./use-sync-settings";

const EMPTY_SNAPSHOT: CapabilitySnapshot = { features: {} };

/** Attribution for an ATS push — which user the post will be credited to. */
export interface AtsAttribution {
  posts_as: string | null;
  is_default: boolean;
}

/**
 * The frozen remote identity of a dormant (unlinked-but-preserved) job link.
 * Present only when the job is currently unlinked but was previously linked —
 * surfaced so the header can show a "was linked to <ATS>" banner and re-link.
 */
export interface DormantLink {
  reason: string;
  connectorName: string;
  connectionId: string;
  remoteJobId: string;
  remoteJobName: string | null;
}

export function useJobAtsLink(jobId: string | null, streamConnected = false) {
  const query = useGetJobAtsLink(jobId ?? "", {
    query: {
      enabled: !!jobId,
      // While the job's initial ATS pull is running the backend reports
      // `importing: true`. Poll so the banner + "Syncing" pill flip off on
      // their own the moment the relink workflow finishes; idle otherwise.
      // When the SSE stream is live, `ats_initial_import_completed` invalidates
      // this query instead, so we skip polling (resumes if the stream dies).
      refetchInterval: (q) =>
        !streamConnected && q.state.data?.importing ? 3000 : false,
    },
  });
  const data = query.data;
  const linked = !!data?.linked;
  // Normalize attribution: convert optional posts_as to string | null.
  const rawAttr = data?.attribution;
  const attribution: AtsAttribution | null = rawAttr
    ? { posts_as: rawAttr.posts_as ?? null, is_default: rawAttr.is_default }
    : null;
  // Dormant identity of a previously-linked-but-now-unlinked job (spec §1).
  // camelCase mapping in line with the summary's other view-model fields.
  const rawDormant = data?.dormant;
  const dormant: DormantLink | null = rawDormant
    ? {
        reason: rawDormant.reason,
        connectorName: rawDormant.connector_name,
        connectionId: rawDormant.connection_id,
        remoteJobId: rawDormant.remote_job_id,
        remoteJobName: rawDormant.remote_job_name ?? null,
      }
    : null;
  return {
    isLinked: linked,
    isLoading: query.isLoading,
    isError: query.isError,
    connectionId: data?.connection_id ?? null,
    connectorName: data?.connector_name ?? "",
    remoteJobUrl: data?.remote_job_url ?? null,
    // presentation snapshot from the API (opaque generated type) -> view-model
    capabilitiesSnapshot: (data?.capabilities_snapshot ?? EMPTY_SNAPSHOT) as unknown as CapabilitySnapshot,
    attribution,
    dormant,
    // True while the job's initial ATS import (first relink pull) is running.
    importing: !!data?.importing,
    syncDirection: (data?.sync_direction ?? undefined) as
      | "bidirectional"
      | "pull_only"
      | "push_only"
      | "unknown"
      | undefined,
  };
}
